import React, { useEffect, useState, useRef } from "react";
import { useWatchContractEvent, useAccount, useWriteContract, useWaitForTransactionReceipt } from "wagmi";
import { toast } from "sonner";
import { VAULT_ABI, VAULT_ADDRESS } from "../config/contracts";
import { LOGO_URL, GOLD_ZONGZI_URL, DRAGON_BOAT_URL, TIER_LABEL } from "./Brand";
import { useUserState, useVaultGlobals } from "../hooks/useVault";

// 5x5 = 25 格；中心 (index=12) 是抽奖按钮，外围 24 格是奖品
// 围绕中心顺时针的 perimeter 序列（用于动画路径）
// 5x5 网格 index map:
//  0  1  2  3  4
//  5  6  7  8  9
// 10 11 12 13 14
// 15 16 17 18 19
// 20 21 22 23 24
// 顺时针 perimeter：0,1,2,3,4, 9,14,19,24, 23,22,21,20, 15,10,5, 然后内圈 6,7,8,13,18,17,16,11
// 总共 24 格
const PERIMETER_ORDER = [0, 1, 2, 3, 4, 9, 14, 19, 24, 23, 22, 21, 20, 15, 10, 5, 6, 7, 8, 13, 18, 17, 16, 11];

// 24 个奖品分布（一等粽子1个 + 二等6个 + 三等10个 + 谢谢7个 = 24，与链上概率比例匹配）
// tier 1=粽子(LOGO一等), 2=二等, 3=三等, 0=谢谢
function buildPrizeMap() {
  const m = new Array(25).fill(null);
  // 一等奖（粽子LOGO）放在顶部正中（index=2，最显眼）
  m[2] = { tier: 1 };
  // 二等奖 6 个
  const tier2Slots = [0, 4, 10, 14, 20, 24];
  tier2Slots.forEach((i) => (m[i] = { tier: 2 }));
  // 三等奖 10 个
  const tier3Slots = [1, 3, 5, 9, 11, 13, 15, 19, 21, 23];
  tier3Slots.forEach((i) => (m[i] = { tier: 3 }));
  // 谢谢参与 7 个
  const noPrizeSlots = [6, 7, 8, 16, 17, 18, 22];
  noPrizeSlots.forEach((i) => (m[i] = { tier: 0 }));
  // 中心
  m[12] = { tier: -1 }; // center button
  return m;
}

const PRIZE_MAP = buildPrizeMap();

function PrizeCell({ tier, active, winner }) {
  if (tier === 1) {
    return (
      <div className={`relative flex flex-col items-center justify-center gap-1 ${winner ? "winner" : ""}`}>
        <img src={LOGO_URL} alt="幸运粽子" className="w-12 h-12 md:w-16 md:h-16 rounded-full"
             style={{ boxShadow: winner ? "0 0 30px rgba(243,186,47,1)" : "0 0 0 2px rgba(243,186,47,0.6)" }} />
        <div className={`text-[10px] md:text-xs font-bold ${winner ? "text-night-900" : "text-amber-300"}`}>幸运粽子</div>
        <div className={`text-[9px] ${winner ? "text-night-800" : "text-emerald-100/60"}`}>一等 0.10 BNB</div>
      </div>
    );
  }
  if (tier === 2) {
    return (
      <div className="flex flex-col items-center justify-center gap-1">
        <img src={GOLD_ZONGZI_URL} alt="安康金粽" className="w-10 h-10 md:w-14 md:h-14 object-contain"
             style={{ filter: winner ? "drop-shadow(0 0 10px rgba(243,186,47,0.9))" : "drop-shadow(0 2px 6px rgba(243,186,47,0.35))" }} />
        <div className={`text-[10px] md:text-xs font-bold ${winner ? "text-night-900" : "text-amber-200"}`}>安康金粽</div>
        <div className={`text-[9px] ${winner ? "text-night-800" : "text-emerald-100/60"}`}>0.05 BNB</div>
      </div>
    );
  }
  if (tier === 3) {
    return (
      <div className="flex flex-col items-center justify-center gap-1">
        <div className="text-3xl md:text-4xl">🍃</div>
        <div className={`text-[10px] md:text-xs font-bold ${winner ? "text-night-900" : "text-teal-300"}`}>吉祥艾草</div>
        <div className={`text-[9px] ${winner ? "text-night-800" : "text-emerald-100/60"}`}>0.02 BNB</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center gap-1 opacity-80">
      <div className="text-3xl md:text-4xl">🥟</div>
      <div className={`text-[10px] md:text-xs ${winner ? "text-night-900" : "text-emerald-100/60"}`}>谢谢参与</div>
    </div>
  );
}

export default function SlotMachine() {
  const { address, isConnected } = useAccount();
  const u = useUserState();
  const g = useVaultGlobals();

  const [activeIdx, setActiveIdx] = useState(-1);
  const [winnerIdx, setWinnerIdx] = useState(-1);
  const [phase, setPhase] = useState("idle"); // idle | spinning | done
  const [resultText, setResultText] = useState("");
  const timersRef = useRef([]);

  const ci = u.checkIn;
  const canDraw = (ci?.canDraw ?? false) && g.lotteryEnabled;
  const onCooldown = u.user ? Math.floor(Date.now() / 1000) < Number(u.user.nextDrawAt) : false;

  function clearTimers() {
    timersRef.current.forEach((t) => clearTimeout(t) || clearInterval(t));
    timersRef.current = [];
  }

  function startSpin(targetTier) {
    clearTimers();
    setPhase("spinning");
    setWinnerIdx(-1);
    setResultText("");

    // 选定目标格子：在 PERIMETER_ORDER 中找到匹配目标 tier 的索引
    const candidateIdxs = PERIMETER_ORDER.filter((i) => PRIZE_MAP[i].tier === targetTier);
    const targetIdx = candidateIdxs[Math.floor(Math.random() * candidateIdxs.length)] ?? PERIMETER_ORDER[0];
    const targetPosInOrder = PERIMETER_ORDER.indexOf(targetIdx);

    // 总共转 3 圈 + 走到目标位置
    const totalSteps = PERIMETER_ORDER.length * 3 + targetPosInOrder;

    let step = 0;
    function nextStep() {
      if (step > totalSteps) {
        setActiveIdx(targetIdx);
        setWinnerIdx(targetIdx);
        setPhase("done");
        const tt = TIER_LABEL[targetTier];
        setResultText(
          targetTier === 0
            ? `${tt.emoji} ${tt.name} - 别灰心，1 小时后再战一局`
            : targetTier === 1
              ? `🎉 恭喜抽中 🍀幸运粽子 一等奖！0.10 BNB 已发放至您的钱包`
              : `🎉 恭喜抽中 ${tt.name}（${TIER_LABEL[targetTier].sub}）！奖金已发放至您的钱包`
        );
        return;
      }
      const idx = PERIMETER_ORDER[step % PERIMETER_ORDER.length];
      setActiveIdx(idx);

      // 速度曲线：先快后慢
      let delay;
      const remaining = totalSteps - step;
      if (remaining > 36) delay = 60;
      else if (remaining > 24) delay = 90;
      else if (remaining > 16) delay = 130;
      else if (remaining > 10) delay = 180;
      else if (remaining > 5) delay = 250;
      else delay = 350;

      step++;
      const t = setTimeout(nextStep, delay);
      timersRef.current.push(t);
    }
    nextStep();
  }

  // 监听链上 DrawCompleted
  useWatchContractEvent({
    address: VAULT_ADDRESS || undefined,
    abi: VAULT_ABI,
    eventName: "DrawCompleted",
    enabled: !!VAULT_ADDRESS,
    onLogs(logs) {
      for (const log of logs) {
        if (!address) continue;
        if (log.args?.user?.toLowerCase() === address.toLowerCase()) {
          startSpin(Number(log.args.tier));
        }
      }
    },
  });

  // 真链上抽奖
  const { writeContract, data: txHash, isPending } = useWriteContract();
  const { isLoading: txMining } = useWaitForTransactionReceipt({ hash: txHash });

  function realDraw() {
    if (!VAULT_ADDRESS) {
      toast.info("活动尚未开始，敬请期待 🎋");
      return;
    }
    writeContract({
      address: VAULT_ADDRESS, abi: VAULT_ABI, functionName: "draw",
    }, {
      onSuccess: () => toast("🎲 抽奖交易已发送…", { description: "等待 VRF 链上开奖回调（约 30-90 秒）" }),
      onError: (e) => toast.error(e?.shortMessage || "抽奖失败"),
    });
  }

  useEffect(() => () => clearTimers(), []);

  const centerDisabled = phase === "spinning" || !VAULT_ADDRESS || (VAULT_ADDRESS && (!canDraw || onCooldown));
  const centerLabel =
    phase === "spinning" ? "开奖中…"
    : !VAULT_ADDRESS
      ? "未开始"
      : (!isConnected ? "连接钱包" : onCooldown ? "冷却中" : !canDraw ? "需签到" : "抽奖");

  return (
    <section id="slot" className="relative z-10 px-6 md:px-10 mt-12">
      {/* 龙舟划水动画 */}
      <div className="relative h-36 md:h-48 mb-2 overflow-hidden rounded-2xl"
           style={{ background: "linear-gradient(180deg, rgba(10,37,64,0.4) 0%, rgba(16,63,122,0.45) 60%, rgba(13,92,58,0.5) 100%)" }}>
        {/* 水面波纹（3 层叠加视差） */}
        <svg className="absolute inset-x-0 bottom-0 w-[200%] h-16 opacity-50" viewBox="0 0 2000 80" preserveAspectRatio="none" style={{ animation: "wave 9s linear infinite" }}>
          <path d="M0,40 Q250,5 500,40 T1000,40 T1500,40 T2000,40 V80 H0 Z" fill="rgba(22,160,101,0.45)" />
        </svg>
        <svg className="absolute inset-x-0 bottom-0 w-[200%] h-20 opacity-60" viewBox="0 0 2000 80" preserveAspectRatio="none" style={{ animation: "wave 6s linear infinite reverse" }}>
          <path d="M0,50 Q250,15 500,50 T1000,50 T1500,50 T2000,50 V80 H0 Z" fill="rgba(243,186,47,0.18)" />
        </svg>
        <svg className="absolute inset-x-0 bottom-0 w-[200%] h-24 opacity-70" viewBox="0 0 2000 80" preserveAspectRatio="none" style={{ animation: "wave 4s linear infinite" }}>
          <path d="M0,60 Q250,25 500,60 T1000,60 T1500,60 T2000,60 V80 H0 Z" fill="rgba(5,21,39,0.7)" />
        </svg>
        {/* 浪花点 */}
        <div className="absolute inset-x-0 bottom-12 h-6">
          {Array.from({ length: 10 }).map((_, i) => (
            <span key={i} className="absolute w-1 h-1 rounded-full bg-amber-200/60"
                  style={{ left: `${(i * 11) % 100}%`, bottom: `${(i * 7) % 16}px`, animation: `sparkle ${2 + (i % 3)}s ease-in-out infinite`, animationDelay: `${i * 0.3}s` }} />
          ))}
        </div>
        {/* 龙舟向前划（龙头朝右，从左边出场→右边出屏→左边再出现，永不调头） */}
        <img
          src={DRAGON_BOAT_URL}
          alt="LuckyZongzi dragon boat"
          className="absolute bottom-2 md:bottom-4 w-40 h-32 md:w-56 md:h-44 object-contain"
          style={{
            filter: "drop-shadow(0 10px 18px rgba(243,186,47,0.5))",
            transform: "scaleX(-1)",
            animation: "boat-slide 22s linear infinite, boat-bob 4s ease-in-out infinite",
          }}
        />
        <style>{`
          @keyframes wave { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
          @keyframes sparkle { 0%, 100% { opacity: 0.2; transform: translateY(0) scale(0.6); } 50% { opacity: 1; transform: translateY(-4px) scale(1.4); } }
          @keyframes boat-slide {
            0%   { left: -22%; }
            100% { left: 100%; }
          }
          @keyframes boat-bob {
            0%, 100% { margin-bottom: 0; }
            25% { margin-bottom: 6px; }
            50% { margin-bottom: 14px; }
            75% { margin-bottom: 6px; }
          }
        `}</style>
      </div>

      <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold gold-shimmer">🎰 马上抽奖</h2>
          <p className="text-emerald-100/60 text-sm mt-1">Chainlink VRF v2.5 链上随机，光环转动 → 定格中奖</p>
        </div>
        {!VAULT_ADDRESS && (
          <span className="text-[11px] text-amber-300/90 border border-amber-300/40 bg-amber-300/10 px-3 py-1 rounded-full" data-testid="status-not-started">⏳ 活动未开始</span>
        )}
      </div>

      <div className="glass gold-border p-4 md:p-6 relative overflow-hidden" data-testid="slot-machine">
        {/* 顶部跑马灯小灯 */}
        <div className="absolute top-2 left-0 right-0 flex justify-center gap-1.5">
          {Array.from({ length: 28 }).map((_, i) => (
            <span key={i} className={`w-1.5 h-1.5 rounded-full transition ${
              phase === "spinning" && i % 2 === Math.floor(Date.now() / 200) % 2
                ? "bg-amber-300 shadow-[0_0_8px_#F3BA2F]"
                : "bg-amber-700/30"
            }`} />
          ))}
        </div>

        <div className="grid grid-cols-5 gap-2 md:gap-3 mt-5 max-w-3xl mx-auto">
          {PRIZE_MAP.map((cell, i) => {
            if (i === 12) {
              // 中心抽奖按钮
              return (
                <button
                  key="center"
                  data-testid="btn-grid-draw"
                  disabled={centerDisabled || isPending || txMining}
                  onClick={realDraw}
                  className={`relative aspect-square rounded-full font-display text-lg md:text-2xl font-bold
                    transition-all duration-300 z-10
                    ${centerDisabled || isPending || txMining ? "opacity-60 cursor-not-allowed" : "hover:scale-105 cursor-pointer"}`}
                  style={{
                    background: phase === "spinning"
                      ? "radial-gradient(circle at 30% 30%, #FFD56A, #F3BA2F 60%, #c89a25)"
                      : "radial-gradient(circle at 30% 30%, #2dd4a3, #16A065 50%, #0D5C3A)",
                    color: phase === "spinning" ? "#0A2540" : "#FFFFFF",
                    boxShadow: phase === "spinning"
                      ? "0 0 40px rgba(243,186,47,0.85), inset 0 0 24px rgba(255,255,255,0.5)"
                      : "0 0 28px rgba(22,160,101,0.55), inset 0 0 18px rgba(255,255,255,0.2), 0 0 0 3px rgba(243,186,47,0.55)",
                  }}
                >
                  <div className="leading-tight">
                    <div className="text-xs md:text-sm opacity-80">
                      {phase === "spinning" ? "VRF" : isPending || txMining ? "提交中" : "🍀"}
                    </div>
                    <div className="font-bold text-lg md:text-xl">{centerLabel}</div>
                  </div>
                </button>
              );
            }
            const isActive = activeIdx === i;
            const isWin = winnerIdx === i && phase === "done";
            return (
              <div
                key={i}
                data-testid={`grid-cell-${i}`}
                className={`aspect-square rounded-xl flex items-center justify-center p-1 md:p-2 transition-all duration-200
                  ${isActive && !isWin ? "active" : ""}
                  ${isWin ? "winner" : ""}
                  slot-cell`}
              >
                <PrizeCell tier={cell.tier} active={isActive} winner={isWin} />
              </div>
            );
          })}
        </div>

        <div className="mt-6 min-h-[48px] flex items-center justify-center text-center">
          {phase === "idle" && (
            <div className="text-emerald-100/60 text-sm">
              点击中央 <span className="text-amber-300 font-bold">抽奖</span> 发起 VRF 请求，
              <span className="text-emerald-300">围绕粽子转一圈</span>，定格中奖格。
            </div>
          )}
          {phase === "spinning" && (
            <div className="text-amber-300 font-display text-lg animate-pulse" data-testid="slot-spinning">
              🔮 VRF 链上摇号中… 光环正绕粽子转动
            </div>
          )}
          {phase === "done" && (
            <div
              className={`font-display text-lg md:text-xl ${
                winnerIdx >= 0 && PRIZE_MAP[winnerIdx].tier > 0 ? "text-amber-300" : "text-emerald-200/85"
              }`}
              data-testid="slot-result"
            >
              {resultText}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
